import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { verbAPI } from "../services/api";
import { useSound } from "../hooks/useSound";
import { useSettings } from "../hooks/useSettings";

interface VerbForm {
  id: number;
  greek_word: string;
  lexical_form: string;
  word_type: "verb" | "participle" | "infinitive";
  tense: string;
  voice: string;
  mood: string;
  person?: string;
  number?: string;
  gender?: string;
  case?: string;
  english_translation: string;
  reference?: string;
  context_text?: string;
  user_correct?: boolean;
  attempts?: number;
  parsingOptions?: any;
}

interface VerbAnswer {
  wordType: "verb" | "participle" | "infinitive";
  lexicalForm: string;
  tense: string;
  voice: string;
  mood: string;
  person?: string;
  number?: string;
  gender?: string;
  case?: string;
  translation?: string;
}

interface ParsingStep {
  id: string;
  label: string;
  field: keyof VerbAnswer;
  options: Array<{ value: string; label: string; description?: string }>;
  required: boolean;
}

type StepStatus = "pending" | "current" | "correct" | "incorrect" | "completed";

const VerbParsingPractice: React.FC = () => {
  const { collectionId } = useParams<{ collectionId: string }>();
  const navigate = useNavigate();
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: authLoading,
  } = useAuth0();
  const { playCorrectSound, playIncorrectSound } = useSound();
  const { settings } = useSettings();

  const [verbs, setVerbs] = useState<VerbForm[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [stepStatuses, setStepStatuses] = useState<StepStatus[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackType, setFeedbackType] = useState<"correct" | "incorrect">(
    "correct"
  );
  const [startTime, setStartTime] = useState<number>(0);
  const [waitingForContinue, setWaitingForContinue] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(0);

  // Check if device is mobile/touch-enabled
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           ('ontouchstart' in window) ||
           (window.innerWidth <= 768 && window.innerHeight <= 1024);
  };

  useEffect(() => {
    if (!collectionId) return;

    // Only fetch verbs if user is authenticated and auth is not loading
    if (!isAuthenticated || authLoading) {
      if (!authLoading) {
        setLoading(false);
      }
      return;
    }

    const fetchVerbs = async () => {
      try {
        const response = await verbAPI.getVerbs(
          parseInt(collectionId),
          getAccessTokenSilently,
          settings.exerciseOrder
        );
        const verbsList = response.data.verbs || [];
        const optionsByCategory = response.data.parsingOptionsByCategory;
        const verbsWithOptions =
          optionsByCategory != null
            ? verbsList.map((v: VerbForm) => {
                const categories =
                  v.word_type === "verb"
                    ? ["lexical_form", "tense", "voice", "mood", "person", "number"]
                    : v.word_type === "participle"
                      ? ["lexical_form", "tense", "voice", "mood", "gender", "case", "number"]
                      : ["lexical_form", "tense", "voice", "mood"];
                const parsingOptions: Record<string, Array<{ value: string; label: string; description?: string | null }>> = {};
                for (const cat of categories) {
                  parsingOptions[cat] = optionsByCategory[cat] ?? [];
                }
                return { ...v, parsingOptions };
              })
            : verbsList;
        setVerbs(verbsWithOptions);
        setStartTime(Date.now());
      } catch (err: any) {
        console.error("Failed to load verbs:", err);
        console.error("Error response:", err.response?.data);
        console.error("Error status:", err.response?.status);
        setError("Failed to load verbs. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchVerbs();
  }, [collectionId, getAccessTokenSilently, isAuthenticated, authLoading, settings.exerciseOrder]);

  useEffect(() => {
    if (verbs.length > 0 && currentIndex < verbs.length) {
      initializeSteps();
      setStartTime(Date.now());
      setShowContext(false); // Reset context visibility when moving to new verb
    }
  }, [currentIndex, verbs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset focused option index when step changes
  useEffect(() => {
    setFocusedOptionIndex(0);
  }, [currentStep]);

  // Helper function to limit options to 6 choices (5 random + 1 correct)
  const getLimitedOptions = (
    allOptions: any[],
    correctValue: string
  ): any[] => {
    if (!allOptions || allOptions.length === 0) return [];

    // Always include the correct answer
    let correctOption = allOptions.find(
      (option) => option.value === correctValue
    );

    // If correct answer is not in the options, create it
    if (!correctOption) {
      correctOption = {
        value: correctValue,
        label: correctValue,
        description: correctValue,
      };
      // Add the correct option to the available options
      allOptions = [...allOptions, correctOption];
    }

    // Get all options except the correct one
    const otherOptions = allOptions.filter(
      (option) => option.value !== correctValue
    );

    // Randomly select 5 other options (or fewer if there aren't enough)
    const shuffled = otherOptions.sort(() => 0.5 - Math.random());
    const selectedOptions = shuffled.slice(0, 5);

    // Combine correct answer with selected options and shuffle the final list
    const finalOptions = [correctOption, ...selectedOptions];
    return finalOptions.sort(() => 0.5 - Math.random());
  };

  // Helper functions defined before they're used
  const generateParsingSteps = (verb?: VerbForm): ParsingStep[] => {
    if (!verb) return [];

    const steps: ParsingStep[] = [
      {
        id: "wordType",
        label: "Word Type",
        field: "wordType",
        options: [
          {
            value: "verb",
            label: "Verb",
            description: "Finite verb with person and number",
          },
          {
            value: "participle",
            label: "Participle",
            description: "Verbal adjective with gender, case, and number",
          },
          {
            value: "infinitive",
            label: "Infinitive",
            description: "Verbal noun with tense and voice",
          },
        ],
        required: true,
      },
      {
        id: "lexicalForm",
        label: "Lexical Form",
        field: "lexicalForm",
        options: getLimitedOptions(
          verb.parsingOptions?.lexical_form || [],
          verb.lexical_form
        ),
        required: true,
      },
    ];

    // Add common steps for all word types
    steps.push(
      {
        id: "tense",
        label: "Tense",
        field: "tense",
        options: verb.parsingOptions?.tense || [],
        required: true,
      },
      {
        id: "voice",
        label: "Voice",
        field: "voice",
        options: verb.parsingOptions?.voice || [],
        required: true,
      }
    );

    // Add mood step (only for verbs)
    if (verb.word_type === "verb") {
      steps.push({
        id: "mood",
        label: "Mood",
        field: "mood",
        options: verb.parsingOptions?.mood || [],
        required: true,
      });
    }

    // Add word-type specific fields after mood
    if (verb.word_type === "verb") {
      steps.push(
        {
          id: "person",
          label: "Person",
          field: "person",
          options: verb.parsingOptions?.person || [],
          required: true,
        },
        {
          id: "number",
          label: "Number",
          field: "number",
          options: verb.parsingOptions?.number || [],
          required: true,
        }
      );
    } else if (verb.word_type === "participle") {
      steps.push(
        {
          id: "gender",
          label: "Gender",
          field: "gender",
          options: verb.parsingOptions?.gender || [],
          required: true,
        },
        {
          id: "case",
          label: "Case",
          field: "case",
          options: verb.parsingOptions?.case || [],
          required: true,
        },
        {
          id: "number",
          label: "Number",
          field: "number",
          options: verb.parsingOptions?.number || [],
          required: true,
        }
      );
    }
    // Infinitives only need tense and voice (no additional fields)

    return steps;
  };

  const currentVerb = verbs[currentIndex];
  
  // Memoize parsing steps to prevent re-shuffling options on every render
  const parsingSteps = useMemo(() => {
    return generateParsingSteps(currentVerb);
  }, [currentVerb, currentIndex]);

  // Keyboard navigation effect (disabled on mobile)
  useEffect(() => {
    if (!currentVerb || submitting || waitingForContinue || showFeedback || isMobile())
      return;

    const currentStepData = parsingSteps[currentStep];
    if (!currentStepData || !currentStepData.options.length) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const optionsCount = currentStepData.options.length;

      // Context toggle (works anytime)
      if (e.key === "o" || e.key === "O") {
        e.preventDefault();
        setShowContext(!showContext);
        return;
      }

      // Navigation keys
      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        setFocusedOptionIndex((prev) => (prev + 1) % optionsCount);
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        setFocusedOptionIndex(
          (prev) => (prev - 1 + optionsCount) % optionsCount
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        const focusedOption = currentStepData.options[focusedOptionIndex];
        if (focusedOption) {
          setSelectedAnswer(focusedOption.value);
          handleAnswerSubmit(focusedOption.value);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    currentVerb,
    currentStep,
    parsingSteps,
    submitting,
    waitingForContinue,
    showFeedback,
    focusedOptionIndex,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const initializeSteps = () => {
    if (!currentVerb) return;

    const steps = generateParsingSteps(currentVerb);
    const statuses: StepStatus[] = steps.map((_, index) =>
      index === 0 ? "current" : "pending"
    );
    setStepStatuses(statuses);
    setCurrentStep(0);
    setSelectedAnswer("");
    setShowFeedback(false);
  };

  const getCorrectValueForStep = (step: ParsingStep): string => {
    if (!currentVerb) return "";

    switch (step.field) {
      case "wordType":
        return currentVerb.word_type;
      case "lexicalForm":
        return currentVerb.lexical_form;
      case "tense":
        return currentVerb.tense;
      case "voice":
        return currentVerb.voice;
      case "mood":
        return currentVerb.mood;
      case "person":
        return currentVerb.person || "";
      case "number":
        return currentVerb.number || "";
      case "gender":
        return currentVerb.gender || "";
      case "case":
        return currentVerb.case || "";
      case "translation":
        return currentVerb.english_translation;
      default:
        return "";
    }
  };

  const handleAnswerSubmit = async (answer?: string) => {
    if (!currentVerb) return;

    const selectedAnswerValue = answer || selectedAnswer;
    const currentStepData = parsingSteps[currentStep];
    const correctValue = getCorrectValueForStep(currentStepData);
    const isCorrect =
      selectedAnswerValue.toLowerCase() === correctValue.toLowerCase();

    // Play sound effect based on result
    if (isCorrect) {
      playCorrectSound();
    } else {
      playIncorrectSound();
    }

    setSubmitting(true);

    // Show immediate feedback
    setFeedbackType(isCorrect ? "correct" : "incorrect");
    setFeedbackMessage(
      isCorrect ? "Correct! ✓" : `The correct answer is: ${correctValue}`
    );
    setShowFeedback(true);

    // Update step status
    const newStatuses = [...stepStatuses];
    newStatuses[currentStep] = isCorrect ? "correct" : "incorrect";
    setStepStatuses(newStatuses);

    setTimeout(() => {
      setSubmitting(false);

      if (isCorrect) {
        setShowFeedback(false);
        setSelectedAnswer("");
        setWaitingForContinue(false);
        // Move to next step immediately
        if (currentStep < parsingSteps.length - 1) {
          const nextStatuses = [...newStatuses];
          nextStatuses[currentStep] = "completed";
          nextStatuses[currentStep + 1] = "current";
          setStepStatuses(nextStatuses);
          setCurrentStep(currentStep + 1);
        } else {
          // All steps completed
          handleCompleteVerb();
        }
      } else {
        // For incorrect answers, show feedback and wait for user to click continue
        setWaitingForContinue(true);
      }
    }, 500);
  };

  const handleContinue = () => {
    setShowFeedback(false);
    setSelectedAnswer("");
    setWaitingForContinue(false);
  };

  const handleCompleteVerb = async () => {
    // Submit final answer to backend
    const timeTaken = Math.floor((Date.now() - startTime) / 1000);
    try {
      await verbAPI.submitAnswer(
        currentVerb.id,
        {
          wordType: currentVerb.word_type,
          lexicalForm: currentVerb.lexical_form,
          tense: currentVerb.tense,
          voice: currentVerb.voice,
          mood: currentVerb.mood,
          person: currentVerb.person,
          number: currentVerb.number,
          gender: currentVerb.gender,
          case: currentVerb.case,
          translation: currentVerb.english_translation,
          timeTaken,
        },
        getAccessTokenSilently
      );

      // Move to next verb or finish
      if (currentIndex < verbs.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        navigate("/verbs");
      }
    } catch (err: any) {
      setError("Failed to submit final answer. Please try again.");
    }
  };

  const renderStepIndicator = () => (
    <div className="flex justify-center mb-6">
      <div className="flex space-x-2">
        {parsingSteps.map((step, index) => (
          <div
            key={step.id}
            className={`w-3 h-3 rounded-full transition-colors ${
              stepStatuses[index] === "completed"
                ? "bg-teal-500"
                : stepStatuses[index] === "correct"
                ? "bg-teal-500"
                : stepStatuses[index] === "current"
                ? "bg-cyan-500 animate-pulse"
                : stepStatuses[index] === "incorrect"
                ? "bg-red-500"
                : "bg-zinc-300"
            }`}
          />
        ))}
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    if (!parsingSteps[currentStep]) return null;

    const currentStepData = parsingSteps[currentStep];

    return (
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-zinc-800 dark:text-zinc-100 mb-6">
          {currentStepData.field === "wordType"
            ? "What type of verbal form is this?"
            : currentStepData.field === "mood"
            ? currentVerb.word_type === "verb"
              ? "What is the mood of this verb?"
              : "Confirm the verbal form type"
            : currentVerb.word_type === "participle" &&
              currentStepData.field === "gender"
            ? "What is the gender of this participle?"
            : currentVerb.word_type === "participle" &&
              currentStepData.field === "case"
            ? "What is the case of this participle?"
            : currentVerb.word_type === "infinitive" &&
              currentStepData.field === "tense"
            ? "What is the tense of this infinitive?"
            : `What is the ${currentStepData.label.toLowerCase()}?`}
        </h3>

        {/* Greek word */}
        <div className="mb-6 text-center">
          <div className="greek-text text-xl font-semibold text-zinc-800 dark:text-cyan-500 mb-6">
            {currentVerb.greek_word.replace(/[⸃⸀.,]/g, "")}
          </div>
        </div>

        {/* Context Toggle */}
        {currentVerb.context_text && currentVerb.reference && (
          <div className="mb-6 text-center">
              {!showContext ? (
              <button
                onClick={() => setShowContext(true)}
                className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-300 underline focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-opacity-50 rounded px-1"
                title="Show verse context (Press 'o')"
              >
                Show {currentVerb.reference}
              </button>
            ) : (
              <div className="space-y-2">
                {(() => {
                  // Split context to bold the Greek word
                  // Clean special characters from both word and context
                  const cleanText = (text: string) =>
                    text.replace(/[⸃⸀.,]/g, "");
                  const greekWord = currentVerb.greek_word;
                  const context = currentVerb.context_text;
                  const cleanedWord = cleanText(greekWord);
                  const cleanedContext = cleanText(context);
                  const wordIndex = cleanedContext.indexOf(cleanedWord);

                  if (wordIndex !== -1) {
                    const before = cleanedContext.substring(0, wordIndex);
                    const after = cleanedContext.substring(
                      wordIndex + cleanedWord.length
                    );
                    return (
                      <div className="greek-context text-md text-zinc-700 dark:text-zinc-100 max-w-2xl mx-auto px-4">
                        {before}
                        <span className="font-bold text-zinc-900 dark:text-zinc-100 not-italic">
                          {cleanedWord}
                        </span>
                        {after} - {currentVerb.reference}, SBLGNT
                      </div>
                    );
                  }

                  // Fallback if word not found in context
                  return (
                    <div className="greek-context text-md text-zinc-700 dark:text-zinc-100 max-w-2xl mx-auto px-4">
                      {cleanedContext} - {currentVerb.reference}, SBLGNT
                    </div>
                  );
                })()}
                <button
                  onClick={() => setShowContext(false)}
                  className="text-xs text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300 underline focus:outline-none"
                >
                  Hide
                </button>
              </div>
            )}
          </div>
        )}

        {/* Feedback display */}
        {showFeedback && (
          <div
            className={`mb-6 p-4 rounded-lg text-center ${
              feedbackType === "correct"
                ? "bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800"
                : "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
            }`}
          >
            <div className="flex items-center justify-center mb-2">
              <span
                className={`font-medium text-lg ${
                  feedbackType === "correct"
                    ? "text-teal-800 dark:text-teal-400"
                    : "text-red-800 dark:text-red-400"
                }`}
              >
                {feedbackMessage}
              </span>
            </div>
            {feedbackType === "incorrect" && waitingForContinue && (
              <button
                onClick={handleContinue}
                className="mt-3 px-4 py-2 bg-cyan-600 dark:bg-cyan-700 text-white rounded hover:bg-cyan-700 dark:hover:bg-cyan-600 transition-colors"
              >
                Continue
              </button>
            )}
          </div>
        )}

        {/* Answer options */}
        <div className="max-w-md mx-auto">
          <div className="grid grid-cols-1 gap-3">
            {currentStepData.options.map((option, index) => (
              <button
                key={option.value}
                onClick={() => {
                  if (!submitting && !showFeedback && !waitingForContinue) {
                    setSelectedAnswer(option.value);
                    setFocusedOptionIndex(index);
                    // Submit the answer immediately when clicked
                    handleAnswerSubmit(option.value);
                  }
                }}
                onMouseEnter={() => setFocusedOptionIndex(index)}
                disabled={submitting || showFeedback || waitingForContinue}
                className={`p-4 border rounded-lg text-center transition-all duration-200 ${
                  selectedAnswer === option.value
                    ? "border-cyan-500 dark:border-cyan-400 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400 ring-2 ring-cyan-300 dark:ring-cyan-600"
                    : !isMobile() && focusedOptionIndex === index &&
                      !submitting &&
                      !waitingForContinue
                    ? "border-cyan-400 dark:border-cyan-500 bg-cyan-50/50 dark:bg-cyan-900/10 text-cyan-600 dark:text-cyan-400 ring-2 ring-cyan-200 dark:ring-cyan-700"
                    : "border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-zinc-100 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 hover:text-cyan-600 dark:hover:text-cyan-400 hover:border-cyan-400 dark:hover:border-cyan-500"
                } ${
                  submitting || waitingForContinue
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                <div className="flex items-center justify-center">
                  <span className="text-lg font-medium">{option.label}</span>
                  {submitting && selectedAnswer === option.value && (
                    <div className="ml-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-600"></div>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (error && verbs.length === 0) {
    return (
      <div className="text-center">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="bg-cyan-600 dark:bg-cyan-700 text-white px-4 py-2 rounded hover:bg-cyan-700 dark:hover:bg-cyan-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!currentVerb) {
    return (
      <div className="text-center">
        <p className="text-zinc-600 dark:text-zinc-400">
          No verbs available in this collection.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress indicator */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-zinc-600 dark:text-zinc-400">
            Verb {currentIndex + 1} of {verbs.length}
          </span>
          <span className="text-sm text-zinc-600 dark:text-zinc-400">
            {Math.round(((currentIndex + 1) / verbs.length) * 100)}% Complete
          </span>
        </div>
        <div className="w-full bg-zinc-200 dark:bg-zinc-700 rounded-full h-2">
          <div
            className="bg-cyan-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentIndex + 1) / verbs.length) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Main content */}
      <div className="bg-white dark:bg-zinc-800 rounded-lg shadow-md border border-zinc-200 dark:border-zinc-700 p-8 mb-8">
        {/* Step indicator */}
        {renderStepIndicator()}

        {/* Current step content */}
        {renderCurrentStep()}

        {/* Error message */}
        {error && (
          <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded text-center">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default VerbParsingPractice;
